
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { formatDate, formatThousands } from '@/utils'
import { createModel } from '@/utils/component'
import { checkPermission } from '@/utils/permission'
import {
  cancelSAPApplication, excelSAPFileUpload, exportSAPTemplate, getSAPRequestPageList, setSAPPaymentDate,
  setSAPSetPurpose, getSAPRequestPageListByUser
} from '@/apis/application-form/paymentRequestSap'
import dayjs from 'dayjs'
import { RootObject } from '@/model/rootObject'
import {
  SAPRequestIdListModel,
  SAPRequestModel, SAPRequestPageParamModel, SAPRequestSetPaymentDateModel, SAPSetPurposeParamModel
} from '@/model/application-form/paymentRequestSap'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchFormDynamicDateRange from '@/components/form/search/SearchFormDynamicDateRange.vue'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import { ElMessage } from 'element-plus'
import GenerateSapApprovalDialog
  from '@/views/application-form/payment-request-sap/GenerateSAPApprovalDialog.vue'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import { size } from 'lodash'

export default defineComponent({
  name: 'PaymentRequestSapList',
  components: {
    GenerateSapApprovalDialog,
    BaseDialog,
    SearchFormDynamicDateRange,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const selectedRows = ref<SAPRequestModel[]>()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'accountNumber', label: t('supplier.supplierCode'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'dynamicDate',
        label: t('order.cutOffDate'),
        component: 'el-date-picker',
        type: 'date',
        model: null,
        suffix: '后',
        isDynamicDateType: true,
        datePickerType: 0,
        dateTypeOptions: [
          { label: '大于日期', value: 0 },
          { label: '小于日期', value: 1 },
          { label: '日期区间', value: 2 }
        ]
      },
      { prop: 'currency', label: t('bank.currency'), component: 'el-input', type: 'text', model: '' }
    ]
    const orderTableStruct = [
      { prop: 'id', label: t('common.indexNumber'), width: '80px' },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'accountNumber', label: t('supplier.supplierCode') },
      { prop: 'vendorName', label: t('supplier.supplierName') },
      { prop: 'documentNumber', label: t('order.documentNumber'), width: '210px' },
      { prop: 'reference', label: t('order.invoiceNumber') },
      { prop: 'amount', label: t('bank.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('bank.currency'), width: '80px' },
      {
        prop: 'dueDate',
        label: t('order.cutOffDate'),
        formatter: (row: SAPRequestModel) => {
          return formatDate(row.dueDate, 'yyyy-MM-dd')
        }
      },
      { prop: 'text', label: t('common.notes') },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        formatter: (row: SAPRequestModel) => {
          return formatDate(row.paymentDate, 'yyyy-MM-dd')
        }
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<SAPRequestModel>(),
      columnStruct: orderTableStruct,
      datePickerDialogVisible: false,
      purposeDialogVisible: false,
      generateApprovalDialogVisible: false,
      changePurposeId: null as number | null,
      purpose: null as string | null,
      selectedDate: null as Date | null,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dueDateStartStr = null
        let dueDateEndStr = null
        if (dataMap.searchFormModel.dynamicDate) {
          if (dataMap.searchFormStruct[2].datePickerType === 0) {
            dueDateStartStr = dayjs(dataMap.searchFormModel.dynamicDate).startOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
          } else if (dataMap.searchFormStruct[2].datePickerType === 1) {
            dueDateEndStr = dayjs(dataMap.searchFormModel.dynamicDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
          } else if (dataMap.searchFormStruct[2].datePickerType === 2 && size(dataMap.searchFormModel.dynamicDate) === 2) {
            dueDateStartStr = dayjs(dataMap.searchFormModel.dynamicDate[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
            dueDateEndStr = dayjs(dataMap.searchFormModel.dynamicDate[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
          }
        }
        const params: SAPRequestPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          accountNumber: dataMap.searchFormModel.accountNumber,
          dueDate: null,
          dueDateStart: dueDateStartStr,
          dueDateEnd: dueDateEndStr,
          currency: dataMap.searchFormModel.currency,
          isProcess: 0,
          active: true,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        let data = null
        if (checkPermission(['APPLY_SAP_R'])) {
          data = await getSAPRequestPageList(params)
        } else if (checkPermission(['APPLY_SAP_SR'])) {
          data = await getSAPRequestPageListByUser(params)
        }

        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleAvatarSuccess(response: RootObject<number>, file: any) {
        if (response.data > 0) {
          const message = response.msg ? response.msg : t('fileUpload.uploadSuccess')
          ElMessage({
            message: message,
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else if (response.msg && response.msg !== 'success') {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      handleAvatarError(response: RootObject<number>, file: any) {
        if (response.msg) {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      getUploadUri() {
        return excelSAPFileUpload()
      },
      getUploadHeader() {
        return { Authorization: useStore().state.user.token }
      },
      checkFile(file: any) {
        if (file) {
          console.log('file type')
          console.log(file)
        }
      },
      async handleExport() {
        const urlBlob = await exportSAPTemplate()
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '付款申请(SAP)模板.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handlePaymentDate = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedDate = null
        dataMap.datePickerDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const cancelApplication = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const params: SAPRequestIdListModel = { idList: tableRef.value.getSelectionRows().map((row: any) => row.id) }
        const res = await cancelSAPApplication(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.cancelSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const generateApprovalForm = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        selectedRows.value = tableRef.value.getSelectionRows().map((row: any) => row.id)
        dataMap.generateApprovalDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const handleEdit = (row?: SAPRequestModel) => {
      if (row) {
        dataMap.changePurposeId = row.id
        dataMap.purpose = row.purpose
        dataMap.purposeDialogVisible = true
      }
    }
    const onDialogConfirm = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    const onDialogClose = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const params: SAPRequestSetPaymentDateModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id),
          paymentDate: formatDate(dataMap.selectedDate)
        }
        const res = await setSAPPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.datePickerDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    const onPurposeDialogConfirm = async() => {
      if (!dataMap.purpose) {
        const msg = t('order.purposePayeeMayViewNullP')
        ElMessage({
          message: msg,
          type: 'error',
          duration: 2000
        })
      }
      if (dataMap.changePurposeId != null && dataMap.purpose) {
        const params: SAPSetPurposeParamModel = {
          id: dataMap.changePurposeId,
          purpose: dataMap.purpose
        }
        const res = await setSAPSetPurpose(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.purposeDialogVisible = false
          await dataMap.getPageList(dataMap.page, dataMap.limit)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      selectedRows,
      handlePaymentDate,
      cancelApplication,
      generateApprovalForm,
      handleEdit,
      onDatePickerDialogConfirm,
      onDialogConfirm,
      onPurposeDialogConfirm,
      onDialogClose,
      ...toRefs(dataMap)
    }
  }
})
